import type React from "react"
import { useTranslation } from "react-i18next"

import type { Meter } from "@/models/meter"
import type { ResourceName } from "@/models/resource"
import type { DateTime, Interval } from "luxon"

import { Skeleton, Stack } from "@mui/material"

import { IntervalDataDateRange } from "./interval-data-date-range"
import { IntervalDataMeterFilter } from "./interval-data-meter-filter"
import { IntervalDataResourceFilter } from "./interval-data-resource-filter"

const FILTER_BAR_SKELETON_HEIGHT = 40

const IntervalDataFiltersSkeleton: React.FC = () => {
  const { t } = useTranslation("common")

  return (
    <Skeleton
      aria-label={t("loadingAriaLabel", {
        title: t("fields.resource.title"),
      })}
      data-testid="interval-data-filters-skeleton"
      height={FILTER_BAR_SKELETON_HEIGHT}
      sx={{ flexGrow: 1 }}
      variant="rounded"
      width="100%"
    />
  )
}

interface IntervalDataFilterBarProps {
  dateRange: Interval
  isScopeLoading: boolean
  maxDate: DateTime | null
  meters: Meter[]
  minDate: DateTime | null
  onDateRangeChange: (nextRange: Interval) => void
  onMeterChange: (meter: Meter | null) => void
  onResourceChange: (resource: ResourceName) => void
  options: readonly ResourceName[]
  selectedMeter: Meter | null
  value: ResourceName | null
}

export const IntervalDataFilterBar: React.FC<IntervalDataFilterBarProps> = ({
  dateRange,
  isScopeLoading,
  maxDate,
  meters,
  minDate,
  onDateRangeChange,
  onMeterChange,
  onResourceChange,
  options,
  selectedMeter,
  value,
}) => {
  if (isScopeLoading) {
    return <IntervalDataFiltersSkeleton />
  }

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}
    >
      {value != null ? (
        <IntervalDataResourceFilter
          onResourceChange={onResourceChange}
          options={options}
          value={value}
        />
      ) : null}
      <IntervalDataDateRange
        dateRange={dateRange}
        maxDate={maxDate}
        minDate={minDate}
        onDateRangeChange={onDateRangeChange}
      />
      {meters.length > 0 ? (
        <IntervalDataMeterFilter
          meters={meters}
          onMeterChange={onMeterChange}
          selectedMeter={selectedMeter}
        />
      ) : null}
    </Stack>
  )
}
